import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, Link2, Download, ShieldAlert, Search, Loader2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { assessmentPdfUrl, assessmentReportUrl } from '../../services/assessmentApi';
import { scoreColor } from './report/ReportBits';
import { getRegionLabel } from '../../context/RegionContext';

const FILTERS = [['all', 'All'], ['completed', 'Completed'], ['in_progress', 'In progress'], ['flagged', 'Flagged']];
const fmt = (d) => (d ? new Date(d).toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

const Row = ({ a }) => {
  const navigate = useNavigate();
  const done = a.status === 'completed';
  const copy = async () => { await navigator.clipboard.writeText(assessmentReportUrl(a.share_token)).catch(() => {}); toast.success('Report link copied'); };
  return (
    <tr className="border-b last:border-0 hover:bg-muted/40 transition-colors" data-testid={`attempt-row-${a.id}`}>
      <td className="py-3 px-4">
        <p className="font-medium text-sm">{a.candidate_name}</p>
        <p className="text-xs text-muted-foreground">{a.candidate_email}</p>
        <span className="mt-1 inline-block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted px-1.5 py-0.5 rounded" data-testid={`attempt-source-${a.id}`}>
          {`${a.region ? getRegionLabel(a.region) : 'Unassigned'} · ${a.link_type === 'personal' ? 'personal' : 'public'} link`}
        </span>
      </td>
      <td className="py-3 px-4 text-xs text-muted-foreground max-w-[220px]"><span className="line-clamp-2">{a.skills.join(', ')}</span></td>
      <td className="py-3 px-4">
        {done ? (
          <div className="w-28">
            <div className="flex justify-between text-xs mb-1"><b>{a.overall_score}%</b><span className="text-muted-foreground">{a.level}</span></div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${a.overall_score}%`, background: scoreColor(a.overall_score) }} /></div>
          </div>
        ) : <span className="inline-flex items-center gap-1.5 text-xs text-amber-600"><Loader2 className="h-3 w-3 animate-spin" /> In progress</span>}
      </td>
      <td className="py-3 px-4 text-xs">
        {a.integrity_risk ? (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold ${a.integrity_risk === 'Low' ? 'bg-emerald-500/10 text-emerald-600' : a.integrity_risk === 'Medium' ? 'bg-amber-500/10 text-amber-600' : 'bg-red-500/10 text-red-600'}`}>
            {a.integrity_risk !== 'Low' && <ShieldAlert className="h-3 w-3" />}{a.integrity_risk}
          </span>
        ) : '—'}
      </td>
      <td className="py-3 px-4 text-xs text-muted-foreground whitespace-nowrap">{fmt(a.submitted_at || a.started_at)}</td>
      <td className="py-3 px-4">
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" disabled={!done} onClick={() => navigate(`/assessments/report/${a.id}`)} data-testid={`view-report-btn-${a.id}`}><Eye className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" disabled={!done} onClick={copy} data-testid={`copy-report-link-btn-${a.id}`}><Link2 className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" disabled={!done} asChild={done} data-testid={`download-report-pdf-btn-${a.id}`}>
            {done ? <a href={assessmentPdfUrl(a.share_token)}><Download className="h-4 w-4" /></a> : <span><Download className="h-4 w-4" /></span>}
          </Button>
        </div>
      </td>
    </tr>
  );
};

export const AttemptsTable = ({ attempts }) => {
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const rows = useMemo(() => attempts.filter((a) => (
    (filter === 'all' || (filter === 'flagged' ? ['Medium', 'High'].includes(a.integrity_risk) : a.status === filter))
    && `${a.candidate_name} ${a.candidate_email}`.toLowerCase().includes(q.toLowerCase())
  )), [attempts, filter, q]);
  return (
    <div className="rounded-2xl border bg-card" data-testid="candidate-results-table">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b">
        <div className="flex gap-1 bg-muted rounded-lg p-1">
          {FILTERS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`attempt-filter-${k}`}
              className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${filter === k ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>{l}</button>
          ))}
        </div>
        <div className="relative w-64">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9 h-9" placeholder="Search candidate…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="attempt-search-input" />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead><tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b">
            {['Candidate', 'Skills claimed', 'Score', 'Integrity', 'Date', ''].map((h) => <th key={h} className="py-2.5 px-4 font-semibold">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((a) => <Row key={a.id} a={a} />)}
            {!rows.length && <tr><td colSpan={6} className="py-14 text-center text-sm text-muted-foreground" data-testid="attempts-empty">No attempts yet — share the public link or create a personal link.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AttemptsTable;
